/**
 * Stripe service for handling subscription-related operations
 */
import config from '../config';

/**
 * Create a checkout session for subscription
 * @param {string} userId - User ID
 * @param {string} email - User email
 * @returns {Promise<string>} - Checkout URL
 */
export const createCheckoutSession = async (userId, email) => {
  try {
    console.log('Creating checkout session for:', { userId, email });
    console.log('Using price ID:', config.stripe.priceId);
    
    const response = await fetch('/.netlify/functions/stripe-checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        email,
        priceId: config.stripe.priceId,
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription`,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('Checkout session creation failed:', responseData);
      throw new Error(responseData.error || 'Failed to create checkout session');
    }

    console.log('Checkout session created successfully');
    return responseData.url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    
    // Provide more detailed error message to the user
    if (error.message.includes('price')) {
      throw new Error('Invalid price configuration. Please contact support.');
    } else if (error.message.includes('customer')) {
      throw new Error('Customer creation failed. Please try again or contact support.');
    } else {
      throw new Error(`Failed to create checkout session: ${error.message}`);
    }
  }
};

/**
 * Create a customer portal session for managing subscription
 * @param {string} userId - User ID
 * @returns {Promise<string>} - Portal URL
 */
export const createPortalSession = async (userId, email) => {
  try {
    console.log('Creating portal session for user:', userId);
    
    // Get the user's email if not provided
    if (!email && localStorage.getItem('userEmail')) {
      email = localStorage.getItem('userEmail');
      console.log('Using email from localStorage:', email);
    }
    
    const response = await fetch('/.netlify/functions/stripe-portal', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        email, // Include email to help with customer creation
        returnUrl: `${window.location.origin}/dashboard`,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('Portal session creation failed:', responseData);
      throw new Error(responseData.error || 'Failed to create portal session');
    }

    console.log('Portal session created successfully');
    return responseData.url;
  } catch (error) {
    console.error('Error creating portal session:', error);
    
    // Provide more detailed error message to the user
    if (error.message.includes('customer')) {
      throw new Error('Customer not found. Please try subscribing first.');
    } else {
      throw new Error(`Failed to create portal session: ${error.message}`);
    }
  }
};

/**
 * Get subscription status for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Subscription status
 */
export const getSubscriptionStatus = async (userId) => {
  try {
    const response = await fetch(`/.netlify/functions/subscription-status?userId=${userId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get subscription status');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting subscription status:', error);
    throw error;
  }
};

/**
 * Save subscription status to local storage
 * @param {string} userId - User ID
 * @param {boolean} isActive - Whether subscription is active
 */
export const saveSubscriptionStatus = (userId, isActive) => {
  try {
    const key = `${config.storageKeys.userSubscription}_${userId}`;
    localStorage.setItem(key, JSON.stringify({
      active: isActive,
      updatedAt: new Date().toISOString()
    }));
  } catch (error) {
    console.error('Error saving subscription status to local storage:', error);
  }
};

/**
 * Get subscription status from local storage
 * @param {string} userId - User ID
 * @returns {boolean} - Whether subscription is active
 */
export const getLocalSubscriptionStatus = (userId) => {
  try {
    const key = `${config.storageKeys.userSubscription}_${userId}`;
    const data = localStorage.getItem(key);
    
    if (!data) return false;
    
    const parsed = JSON.parse(data);
    return parsed.active === true;
  } catch (error) {
    console.error('Error getting subscription status from local storage:', error);
    return false;
  }
};

/**
 * Clear subscription status from local storage
 * @param {string} userId - User ID
 */
export const clearSubscriptionStatus = (userId) => {
  try {
    const key = `${config.storageKeys.userSubscription}_${userId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing subscription status from local storage:', error);
  }
};
